import instantsearch from 'instantsearch.js'
import { clearRefinements, currentRefinements, configure, index, pagination } from 'instantsearch.js/es/widgets/index.js'
import { connectHits, connectStats } from 'instantsearch.js/es/connectors/index.js'

import { resolvePrices as resolveProductPrices } from '../algolia-price-resolving.js'
import { createProductListingRouting } from './routing.js'
import { createFacetWidgets } from './facets.js'
import { createProductCard } from './product-card.js'
import { resolveUrl } from './urls.js'

function parseJson(value, fallback = {}) {
  if (!value) {
    return fallback
  }

  try {
    return JSON.parse(value)
  } catch (error) {
    console.error('Invalid product listing JSON configuration.', error)

    return fallback
  }
}

export default class ProductListingSearch {
  constructor(rootElement, provider) {
    if (!(rootElement instanceof HTMLElement)) {
      throw new TypeError('ProductListingSearch requires a valid root element.')
    }

    this.rootElement = rootElement
    this.provider = provider
    this.elements = this.getElements()
    this.assertRequiredElements()
    this.configuration = this.getConfiguration()
    this.translations = this.getTranslations()
    this.search = this.createSearch()

    // Full-text search shares only its root query with products and documents.
    this.productIndexId = provider.productIndexId ?? provider.indexName
    this.productWidgets = provider.productIndexId ? index({ indexName: provider.indexName, indexId: provider.productIndexId }) : this.search
    this.priceRenderFrame = null
    this.lastProductListViewKey = null
  }

  async start() {
    this.registerBaseWidgets()
    this.bindSearchEvents()

    try {
      const facets = this.getFacetAttributes(await this.provider.getFacetAttributes())

      this.registerFacetWidgets(facets)
    } catch (error) {
      // A failed facet discovery must not prevent products from loading.
      console.error('Product listing facets could not be loaded.', error)
    }

    if (this.productWidgets !== this.search) {
      this.search.addWidgets([this.productWidgets])
    }

    this.search.start()
  }

  destroy() {
    if (this.priceRenderFrame) {
      window.cancelAnimationFrame(this.priceRenderFrame)
    }

    this.search.dispose()
  }

  getElements() {
    return {
      hits: this.rootElement.querySelector('#hits'),
      stats: this.rootElement.querySelector('#stats'),
      mobileToolbarStats: this.rootElement.querySelector('#mobile-toolbar-stats'),
      mobileStats: this.rootElement.querySelector('#mobile-stats'),
      refinements: this.rootElement.querySelector('#refinement-list'),
      currentRefinements: this.rootElement.querySelector('#current-refinements'),
      activeRefinements: this.rootElement.querySelector('#active-refinements'),
      clearRefinements: this.rootElement.querySelector('#clear-refinements'),
      clearRefinementsSidebar: this.rootElement.querySelector('#clear-refinements-sidebar'),
      clearRefinementsMobile: this.rootElement.querySelector('#clear-refinements-mobile'),
      pagination: this.rootElement.querySelector('#pagination-bottom'),
      loading: this.rootElement.querySelector('#product-listing-loading, #category-products-loading'),
      error: this.rootElement.querySelector('#product-listing-error, #category-products-error'),
    }
  }

  assertRequiredElements() {
    const missingElements = ['hits', 'pagination'].filter((name) => !this.elements[name])

    if (missingElements.length > 0) {
      throw new Error(`Missing product listing elements: ${missingElements.join(', ')}`)
    }
  }

  getConfiguration() {
    const applicationConfiguration = window.polyestershoppen

    if (!applicationConfiguration?.config || !this.provider?.searchClient || !this.provider.indexName) {
      throw new Error('Incomplete product listing configuration.')
    }

    const { locale, webshop } = applicationConfiguration.config
    const { endpoint } = applicationConfiguration
    const listingType = this.rootElement.dataset.productListing

    if (!['category', 'brand', 'search'].includes(listingType)) {
      throw new Error('Unsupported product listing type.')
    }

    return {
      listingType,
      category: this.rootElement.dataset.category?.trim() || null,
      brand: this.rootElement.dataset.brand?.trim() || null,
      categoryId: Number(this.rootElement.dataset.categoryId) || null,
      productUrlPrefix: this.rootElement.dataset.productUrlPrefix ?? '',
      facetLabels: parseJson(this.rootElement.dataset.facetLabels),
      facetOrder: parseJson(this.rootElement.dataset.facetOrder, []),
      locale,
      webshop,
      productIndex: this.provider.indexName,
      priceEndpoint: endpoint?.pricing ?? null,
      addToCartEndpoint: endpoint?.addToCart ?? null,
    }
  }

  getTranslations() {
    return {
      result: this.rootElement.dataset.resultText ?? '1 product',
      results: this.rootElement.dataset.resultsText ?? '{num} producten',
      noResults: this.rootElement.dataset.noResultsText ?? 'Geen producten gevonden',
      noResultsDescription: this.rootElement.dataset.noResultsDescription ?? 'Pas de filters aan om andere producten te bekijken.',
      showProducts: this.rootElement.dataset.showProductsText ?? 'Toon {num} producten',
      reset: this.rootElement.dataset.resetText ?? 'Reset filters',
      showMore: this.rootElement.dataset.showMoreText ?? 'Toon meer',
      showLess: this.rootElement.dataset.showLessText ?? 'Toon minder',
      addToCart: this.rootElement.dataset.addToCartText ?? 'In winkelwagen',
      addToCartAriaLabel: this.rootElement.dataset.addToCartAriaLabel ?? 'Voeg {product} toe aan de winkelwagen',
    }
  }

  createSearch() {
    return instantsearch({
      searchClient: this.provider.searchClient,
      indexName: this.provider.indexName,
      insights: this.provider.insights ?? false,
      routing: createProductListingRouting(this.provider.indexName, {
        includeQuery: this.configuration.listingType === 'search',
        productIndexId: this.provider.productIndexId,
      }),
      future: {
        preserveSharedStateOnUnmount: true,
      },
      stalledSearchDelay: 300,
    })
  }

  registerBaseWidgets() {
    const widgets = [
      // Keep hitsPerPage in sync with CategoryListingPage::PRODUCTS_PER_PAGE, which the category canonical relies on.
      configure({ ...this.provider.parameters, hitsPerPage: 20 }),
      this.createStatsWidget(),
      this.createHitsWidget(),
      this.createCurrentRefinementsWidget(),
      this.createPaginationWidget(),
      this.createClearRefinementsWidget(this.elements.clearRefinements, ['btn', 'btn-link', 'btn-sm', 'text-decoration-none', 'p-0']),
      this.createClearRefinementsWidget(this.elements.clearRefinementsSidebar, ['btn', 'btn-link', 'btn-sm', 'text-decoration-none', 'p-0']),
      this.createClearRefinementsWidget(this.elements.clearRefinementsMobile, ['btn', 'btn-outline-secondary', 'w-100']),
    ].filter(Boolean)

    this.productWidgets.addWidgets(widgets)
  }

  createStatsWidget() {
    return connectStats(({ nbHits }) => this.renderStats(nbHits))({})
  }

  createHitsWidget() {
    return connectHits(({ hits, results, sendEvent }) => this.renderHits(hits, results, sendEvent))({
      transformItems: (items) => items.map((item) => this.transformProduct(item)),
    })
  }

  renderHits(hits, results, sendEvent) {
    this.elements.hits.replaceChildren()

    // Keep the initial skeletons until the first actual response.
    if (!results || results.__isArtificial) {
      return
    }

    if (hits.length === 0) {
      this.renderEmptyResults()

      return
    }

    const listName = this.getListName()
    const list = document.createElement('ul')
    list.className = 'ais-Hits-list row gy-4 gx-3 g-xl-4 list-unstyled p-0 mb-0'

    hits.forEach((hit) => {
      const item = document.createElement('li')
      item.className = 'ais-Hits-item col-12 col-sm-6 col-md-4 col-xl-3 d-flex'
      item.append(createProductCard(hit, { sendEvent, addToCartAriaLabel: this.translations.addToCartAriaLabel, listName }))
      list.append(item)
    })

    this.elements.hits.append(list)
    this.trackProductListView(hits, results, listName)
  }

  renderEmptyResults() {
    const container = document.createElement('div')
    container.className = 'text-center py-5'

    const icon = document.createElement('i')
    icon.className = 'ci-search fs-1 text-body-tertiary d-block mb-3'
    icon.setAttribute('aria-hidden', 'true')

    const title = document.createElement('h2')
    title.className = 'h5 mb-2'
    title.textContent = this.translations.noResults

    const description = document.createElement('p')
    description.className = 'text-body-secondary mb-0'
    description.textContent = this.translations.noResultsDescription

    container.append(icon, title, description)
    this.elements.hits.append(container)
  }

  transformProduct(item) {
    const product = this.provider.transformProduct?.(item) ?? item

    return {
      ...product,
      productId: product.id ?? product.objectID,
      productUrl: resolveUrl(product.url, this.configuration.productUrlPrefix) ?? '#',
      addToCartUrl: this.configuration.addToCartEndpoint || null,
      thumbnailUrl: resolveUrl(product.thumbnail),
    }
  }

  createCurrentRefinementsWidget() {
    if (!this.elements.currentRefinements) {
      return null
    }

    return currentRefinements({
      container: this.elements.currentRefinements,
      transformItems: (items) => items.map((item) => ({ ...item, label: this.getFacetLabel(item.attribute) })),
      cssClasses: {
        root: 'w-100',
        noRefinementRoot: 'd-none',
        list: ['list-unstyled', 'gap-2', 'mb-0'],
        item: ['d-flex', 'align-items-center', 'flex-wrap', 'gap-2'],
        label: ['fs-sm', 'fw-semibold', 'me-1'],
        category: ['badge', 'text-bg-secondary', 'd-inline-flex', 'align-items-center', 'gap-1', 'py-2', 'px-3'],
        categoryLabel: 'fw-normal',
        delete: ['btn-close', 'btn-close-white', 'ms-1'],
      },
    })
  }

  createPaginationWidget() {
    return pagination({
      container: this.elements.pagination,
      padding: 2,
      showFirst: false,
      showLast: false,
      scrollTo: this.rootElement.id ? `#${CSS.escape(this.rootElement.id)}` : false,
      cssClasses: {
        root: 'd-flex justify-content-center',
        list: ['pagination', 'mb-0'],
        item: 'page-item',
        selectedItem: 'active',
        disabledItem: 'disabled',
        link: 'page-link',
      },
    })
  }

  createClearRefinementsWidget(container, buttonClasses) {
    if (!container) {
      return null
    }

    return clearRefinements({
      container,
      templates: {
        resetLabel: (data, { html }) => html`<span>${this.translations.reset}</span>`,
      },
      cssClasses: {
        root: 'w-100',
        button: buttonClasses,
        disabledButton: 'd-none',
      },
    })
  }

  getFacetAttributes(availableFacets) {
    if (this.configuration.listingType !== 'category' || this.configuration.facetOrder.length === 0) {
      return availableFacets
    }

    const available = new Set(availableFacets)

    return this.configuration.facetOrder.filter((attribute) => available.has(attribute))
  }

  registerFacetWidgets(facets) {
    if (!this.elements.refinements || facets.length === 0) {
      return
    }

    this.productWidgets.addWidgets(
      createFacetWidgets({
        facets,
        refinementContainer: this.elements.refinements,
        getFacetLabel: (attribute) => this.getFacetLabel(attribute),
        translations: this.translations,
      }),
    )
  }

  getFacetLabel(attribute) {
    const shortAttribute = attribute.replace(/^facets\./, '').replace(/^facet_properties\./, '')
    const labels = this.configuration.facetLabels

    return labels[attribute] ?? labels[shortAttribute] ?? shortAttribute.replaceAll('_', ' ').replace(/\b\w/g, (character) => character.toUpperCase())
  }

  renderStats(numberOfHits) {
    const count = Number(numberOfHits) || 0
    const resultText =
      count === 0 ? this.translations.noResults : count === 1 ? this.translations.result : this.translations.results.replace('{num}', String(count))

    for (const element of [this.elements.stats, this.elements.mobileToolbarStats]) {
      if (element) {
        element.textContent = resultText
      }
    }

    if (this.elements.mobileStats) {
      this.elements.mobileStats.textContent = this.translations.showProducts.replace('{num}', String(count))
    }
  }

  bindSearchEvents() {
    this.search.on('error', ({ error }) => {
      console.error('Product listing search failed.', error)
      this.showError()
    })

    this.search.on('render', () => {
      if (this.search.status === 'error') {
        this.showError()

        return
      }

      if (this.search.status !== 'idle') {
        return
      }

      this.hideError()
      this.removeLoadingState()
      this.updateActiveRefinementsVisibility()
      this.updateFacetVisibility()
      this.schedulePriceResolution()
    })
  }

  updateActiveRefinementsVisibility() {
    if (this.elements.activeRefinements) {
      const refinements = this.search.renderState?.[this.productIndexId]?.currentRefinements
      this.elements.activeRefinements.hidden = !refinements?.items?.length
    }
  }

  updateFacetVisibility() {
    this.elements.refinements?.querySelectorAll('[data-facet-attribute]').forEach((element) => {
      element.hidden = !element.querySelector('[data-facet-values] li')
    })
  }

  schedulePriceResolution() {
    if (this.priceRenderFrame) {
      window.cancelAnimationFrame(this.priceRenderFrame)
    }

    this.priceRenderFrame = window.requestAnimationFrame(() => this.resolvePrices())
  }

  resolvePrices() {
    if (!this.configuration.priceEndpoint) {
      return
    }

    const identifiers = [...this.rootElement.querySelectorAll('[data-product-price-id]')]
      .map((element) => Number(element.dataset.productPriceId))
      .filter(Number.isInteger)

    if (identifiers.length > 0) {
      resolveProductPrices({ endpoint: this.configuration.priceEndpoint, root: this.rootElement, identifiers }).catch((error) => {
        console.error('Product prices could not be resolved.', error)
      })
    }
  }

  removeLoadingState() {
    this.elements.loading?.remove()
    this.elements.loading = null
  }

  showError() {
    this.removeLoadingState()

    if (this.elements.error) {
      this.elements.error.hidden = false
    }
  }

  hideError() {
    if (this.elements.error) {
      this.elements.error.hidden = true
    }
  }

  hasActiveRefinements() {
    const refinements = this.search.getUiState()[this.productIndexId]?.refinementList

    return Object.values(refinements ?? {}).some((values) => Array.isArray(values) && values.length > 0)
  }

  trackProductListView(hits, results, listName) {
    const page = (results?.page ?? 0) + 1
    const state = this.hasActiveRefinements() ? 'filtered' : 'default'
    const products = hits.map((hit) => ({
      sku: hit.ga4_item_id,
      name: hit.name,
      brand: hit.brand,
      categories: hit.ga4_item_categories,
      index: Number.isInteger(hit.__position) ? hit.__position - 1 : null,
    }))
    const key = JSON.stringify({
      query: this.configuration.listingType === 'search' ? (results?.query ?? '') : undefined,
      state,
      page,
      products: hits.map((hit) => hit.sku ?? hit.objectID ?? hit.id),
    })

    if (key === this.lastProductListViewKey) {
      return
    }

    this.lastProductListViewKey = key
    window.dispatchEvent(new CustomEvent('product.list.viewed', { detail: { list: listName, state, page, products } }))
  }

  getListName() {
    if (this.configuration.listingType === 'category') {
      return `Category: ${this.configuration.category}`
    }

    return this.configuration.listingType === 'search' ? 'Search results' : `Brand: ${this.configuration.brand}`
  }
}
